'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, Fragment, useCallback, useId, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { ChevronDownGlyph, StarGlyph } from '../../../internal/icons.js';
import { Button } from '../../actions/button/index.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger, } from '../../overlays/dropdown-menu/index.js';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle, } from '../../overlays/dialog/index.js';
import { ConfirmDialog } from '../../overlays/confirm-dialog/index.js';
import { Field, FieldError, FieldLabel } from '../../forms/form-field/index.js';
import { Input } from '../../forms/text-field/index.js';
export const defaultSavedViewsMessages = {
    triggerPrefix: 'View:',
    noView: 'All items',
    modified: 'Edited',
    shared: 'Shared',
    isDefault: 'Default view',
    saveChanges: (name) => `Save changes to “${name}”`,
    saveAsNew: 'Save as new view…',
    discardChanges: 'Discard changes',
    rename: 'Rename…',
    setDefault: 'Set as default',
    delete: 'Delete view…',
    viewsLabel: 'Views',
    saveTitle: 'Save view',
    renameTitle: 'Rename view',
    nameLabel: 'Name',
    save: 'Save',
    cancel: 'Cancel',
    nameRequired: 'Enter a name.',
    nameTaken: 'A view with this name already exists.',
    deleteTitle: (name) => `Delete “${name}”?`,
    deleteDescription: 'The view is removed for everyone who can see it. Items are not affected.',
    deleteConfirm: 'Delete view',
    saveFailed: 'The view could not be saved. Try again.',
};
/**
 * Switcher for named views of a list (filters, sort and columns), with save,
 * save as new, rename, delete, set default and an "Edited" marker. The app
 * persists views through the callbacks; the component holds no data.
 */
export const SavedViews = forwardRef(function SavedViews({ views, value: valueProp, defaultValue = null, onValueChange, modified = false, onSaveNew, onSaveChanges, onDiscardChanges, onRename, onDelete, onSetDefault, maxNameLength = 80, messages, size = 'sm', disabled = false, className, }, ref) {
    const m = { ...defaultSavedViewsMessages, ...messages };
    const [inner, setInner] = useState(defaultValue);
    const value = valueProp !== undefined ? valueProp : inner;
    const current = views.find((v) => v.id === value);
    const triggerRef = useRef(null);
    const setTrigger = useCallback((el) => {
        triggerRef.current = el;
        if (typeof ref === 'function')
            ref(el);
        else if (ref)
            ref.current = el;
    }, [ref]);
    const [nameDialog, setNameDialog] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const select = (id) => {
        if (valueProp === undefined)
            setInner(id);
        onValueChange?.(id);
    };
    const editable = !!current && !current.readOnly;
    // Views grouped in order of first appearance.
    const groups = [];
    for (const v of views) {
        const g = groups.find((x) => x.name === v.group);
        if (g)
            g.views.push(v);
        else
            groups.push({ name: v.group, views: [v] });
    }
    const returnFocus = (e) => {
        e.preventDefault();
        triggerRef.current?.focus();
    };
    const actions = [];
    if (modified && editable && onSaveChanges)
        actions.push(_jsx(DropdownMenuItem, { onSelect: () => void onSaveChanges(current.id), children: m.saveChanges(current.name) }, "save"));
    if (onSaveNew)
        actions.push(_jsx(DropdownMenuItem, { onSelect: () => setNameDialog({ mode: 'save' }), children: m.saveAsNew }, "new"));
    if (modified && onDiscardChanges)
        actions.push(_jsx(DropdownMenuItem, { onSelect: onDiscardChanges, children: m.discardChanges }, "discard"));
    if (editable && onRename)
        actions.push(_jsx(DropdownMenuItem, { onSelect: () => setNameDialog({ mode: 'rename', view: current }), children: m.rename }, "rename"));
    if (current && !current.isDefault && onSetDefault)
        actions.push(_jsx(DropdownMenuItem, { onSelect: () => void onSetDefault(current.id), children: m.setDefault }, "default"));
    if (editable && onDelete)
        actions.push(_jsx(DropdownMenuItem, { tone: "danger", onSelect: () => setDeleting(current), children: m.delete }, "delete"));
    return (_jsxs(_Fragment, { children: [_jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, disabled: disabled, children: _jsxs(Button, { ref: setTrigger, size: size, variant: "secondary", className: cx('mn-saved-views', className), "data-modified": modified || undefined, trailingIcon: _jsx(ChevronDownGlyph, {}), children: [_jsxs("span", { className: "mn-visually-hidden", children: [m.triggerPrefix, " "] }), _jsx("span", { className: "mn-saved-views__name", children: current?.name ?? m.noView }), ' ', current?.count !== undefined ? (_jsx("span", { className: "mn-saved-views__count", children: current.count })) : null, ' ', modified ? _jsx("span", { className: "mn-saved-views__modified", children: m.modified }) : null] }) }), _jsxs(DropdownMenuContent, { className: "mn-saved-views__menu", children: [_jsx(DropdownMenuRadioGroup, { value: value ?? '', onValueChange: select, children: groups.map((g, gi) => (_jsxs(Fragment, { children: [gi > 0 ? _jsx(DropdownMenuSeparator, {}) : null, _jsx(DropdownMenuLabel, { children: g.name ?? m.viewsLabel }), g.views.map((v) => (_jsxs(DropdownMenuRadioItem, { value: v.id, className: "mn-saved-views__item", children: [_jsx("span", { className: "mn-saved-views__item-name", children: v.name }), v.isDefault ? (_jsxs("span", { className: "mn-saved-views__default", children: [_jsx(StarGlyph, { "aria-hidden": true }), _jsxs("span", { className: "mn-visually-hidden", children: [", ", m.isDefault] })] })) : null, v.shared ? _jsx("span", { className: "mn-saved-views__shared", children: m.shared }) : null, v.count !== undefined ? (_jsx("span", { className: "mn-saved-views__item-count", children: v.count })) : null] }, v.id)))] }, g.name ?? `group-${gi}`))) }), actions.length ? (_jsxs(_Fragment, { children: [_jsx(DropdownMenuSeparator, {}), actions] })) : null] })] }), _jsx(ViewNameDialog, { state: nameDialog, views: views, maxLength: maxNameLength, messages: m, onClose: () => setNameDialog(null), onCloseAutoFocus: returnFocus, onSubmit: async (name) => {
                    if (nameDialog?.mode === 'rename')
                        await onRename?.(nameDialog.view.id, name);
                    else
                        await onSaveNew?.(name);
                } }), _jsx(ConfirmDialog, { open: deleting !== null, onOpenChange: (open) => {
                    if (!open)
                        setDeleting(null);
                }, tone: "danger", title: m.deleteTitle(deleting?.name ?? ''), description: m.deleteDescription, confirmLabel: m.deleteConfirm, cancelLabel: m.cancel, onCloseAutoFocus: returnFocus, onConfirm: async () => {
                    if (deleting)
                        await onDelete?.(deleting.id);
                } })] }));
});
function ViewNameDialog({ state, views, maxLength, messages: m, onClose, onSubmit, onCloseAutoFocus, }) {
    const open = state !== null;
    const formId = useId();
    const initial = state?.mode === 'rename' ? state.view.name : '';
    const [name, setName] = useState(initial);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastState, setLastState] = useState(null);
    if (state !== lastState) {
        // Reset the form each time the dialog opens for a new purpose.
        setLastState(state);
        setName(initial);
        setError(null);
        setBusy(false);
    }
    const submit = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed)
            return setError(m.nameRequired);
        const clash = views.some((v) => v.name.trim().toLowerCase() === trimmed.toLowerCase() &&
            !(state?.mode === 'rename' && v.id === state.view.id));
        if (clash)
            return setError(m.nameTaken);
        setBusy(true);
        setError(null);
        try {
            await onSubmit(trimmed);
            setBusy(false);
            onClose();
        }
        catch {
            setBusy(false);
            setError(m.saveFailed);
        }
    };
    return (_jsx(Dialog, { open: open, onOpenChange: (o) => {
            if (!o)
                onClose();
        }, children: _jsxs(DialogContent, { size: "sm", busy: busy, closeOnOutsideClick: false, onCloseAutoFocus: onCloseAutoFocus, "aria-describedby": undefined, footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", disabled: busy, onClick: onClose, children: m.cancel }), _jsx(Button, { variant: "primary", type: "submit", form: formId, loading: busy, children: m.save })] }), children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: state?.mode === 'rename' ? m.renameTitle : m.saveTitle }) }), _jsx(DialogBody, { children: _jsx("form", { id: formId, noValidate: true, onSubmit: submit, children: _jsxs(Field, { invalid: error !== null, required: true, children: [_jsx(FieldLabel, { children: m.nameLabel }), _jsx(Input, { value: name, maxLength: maxLength, autoComplete: "off", onChange: (e) => {
                                        setName(e.target.value);
                                        if (error)
                                            setError(null);
                                    } }), error ? _jsx(FieldError, { children: error }) : null] }) }) })] }) }));
}
//# sourceMappingURL=saved-views.js.map